import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { BarChart3, Download, Gauge, KeyRound, LogOut, MessageSquare, Moon, Settings, Sun, TriangleAlert, UserRound } from 'lucide-react';
import { useCallback, useState } from 'react';
import { Navigate, NavLink, Route, Routes, useNavigate } from 'react-router-dom';
import { BusinessHoursBadge } from '../components/BusinessHoursBadge';
import { CallPanel } from '../components/CallPanel';
import { ChangePasswordModal } from '../components/ChangePasswordModal';
import { NotificationCenter } from '../components/NotificationCenter';
import { SoftphoneButton } from '../components/SoftphoneButton';
import { NotifyMenu } from '../components/NotifyMenu';
import { Toasts } from '../components/toasts';
import { Spinner } from '../components/ui';
import { get, hasPermission, logout } from '../lib/api';
import { SyncTask } from '../lib/types';
import { AdminPage } from '../pages/admin/AdminPage';
import { DashboardPage } from '../pages/DashboardPage';
import { LoginPage } from '../pages/LoginPage';
import { PendingSyncPage } from '../pages/PendingSyncPage';
import { ReportsPage } from '../pages/ReportsPage';
import { WorkspacePage } from '../pages/workspace/WorkspacePage';
import { FocusProvider, useFocus } from './focus';
import { RealtimeBridge } from './realtime-bridge';
import { SoftphoneProvider } from './softphone';
import { useInstallPrompt } from './pwa';
import { useSession } from './session';

export function App() {
  const { user, checking } = useSession();
  if (checking) {
    return (
      <div className="flex h-full items-center justify-center text-fg-soft">
        <Spinner />
      </div>
    );
  }
  if (!user) {
    return (
      <>
        <LoginPage />
        <Toasts />
      </>
    );
  }
  return (
    <FocusProvider>
      <SoftphoneProvider>
        <Shell />
      </SoftphoneProvider>
      <Toasts />
    </FocusProvider>
  );
}

function Shell() {
  const { user, connected } = useSession();
  const { close } = useFocus();
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));
  const [changingPassword, setChangingPassword] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const pwa = useInstallPrompt();
  const openAttendance = useCallback((id: string) => navigate(`/atendimento/${id}`), [navigate]);

  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle('dark', next);
    try {
      localStorage.setItem('atd.theme', next ? 'dark' : 'light');
    } catch {
      // ignora
    }
  };

  const signOut = async () => {
    await close();
    await logout();
  };

  const canAdmin =
    hasPermission(user, 'user:read') || hasPermission(user, 'settings:manage') || hasPermission(user, 'integration:manage') || hasPermission(user, 'quick_reply:manage');
  // Supervisão vê todas as pendências; o operador, as dos atendimentos dele.
  const canPending = hasPermission(user, 'attendance:read');
  const pendingAll = hasPermission(user, 'attendance:read_all');
  const pending = useQuery({ queryKey: ['pending-sync'], queryFn: () => get<SyncTask[]>('/ticket-sync/tasks'), enabled: canPending, staleTime: 60_000 });
  const pendingAction = pending.data?.filter((t) => t.status === 'waiting' || t.status === 'failed').length ?? 0;
  const pendingLabel = pendingAll ? 'Pendências Agidesk' : 'Minhas pendências';
  const canDashboard = hasPermission(user, 'dashboard:read');
  const canReports = hasPermission(user, 'report:read');
  const nav = ({ isActive }: { isActive: boolean }) =>
    clsx('flex shrink-0 items-center gap-1.5 rounded-md px-1.5 py-1.5 text-sm md:px-2.5', isActive ? 'bg-accent/10 font-medium text-accent' : 'text-fg-soft hover:bg-muted');

  return (
    <div className="flex h-full flex-col">
      <RealtimeBridge onOpenAttendance={openAttendance} />
      <CallPanel onOpenAttendance={openAttendance} />
      {changingPassword && <ChangePasswordModal onClose={() => setChangingPassword(false)} />}
      {/* Faixa fina com o degradê da marca. */}
      <div className="bg-brand-gradient h-1 shrink-0" />
      <header className="flex h-12 shrink-0 items-center gap-1 border-b border-border bg-panel px-2 md:gap-2 md:px-3">
        <div className="mr-1 flex shrink-0 items-center gap-2.5 md:mr-4">
          {/* Celular: só o símbolo, para caber a navegação. */}
          <img src="/brand/simbolo.png" alt="AIO Soluções" className="h-7 w-auto md:hidden" />
          <img src={dark ? '/brand/logo-horizontal-branco.png' : '/brand/logo-horizontal.png'} alt="AIO Soluções" className="hidden h-6 w-auto md:block" />
          <span className="hidden border-l border-border pl-2.5 text-xs font-medium tracking-wide text-fg-soft uppercase xl:inline" style={{ fontFamily: 'var(--font-brand)' }}>
            Atendimento
          </span>
        </div>
        {/* Celular com todos os atalhos (admin): a navegação rola de lado; os ícones da direita ficam sempre à vista. */}
        <nav className="flex min-w-0 items-center gap-1 overflow-x-auto md:gap-2">
          <NavLink to="/atendimento" className={nav} title="Atendimentos">
            <MessageSquare className="size-4" /> <span className="hidden lg:inline">Atendimentos</span>
          </NavLink>
          {canDashboard && (
            <NavLink to="/painel" className={nav} title="Painel">
              <Gauge className="size-4" /> <span className="hidden lg:inline">Painel</span>
            </NavLink>
          )}
          {canReports && (
            <NavLink to="/relatorios" className={nav} title="Relatórios">
              <BarChart3 className="size-4" /> <span className="hidden lg:inline">Relatórios</span>
            </NavLink>
          )}
          {canPending && (
            <NavLink to="/pendencias" className={nav} title={pendingAction ? `${pendingLabel}: ${pendingAction} precisando de ação` : pendingLabel}>
              <TriangleAlert className={clsx('size-4', pendingAction > 0 && 'text-danger')} /> <span className="hidden xl:inline">{pendingLabel}</span>
              {pendingAction > 0 && <span className="rounded-full bg-danger px-1.5 text-[10px] leading-4 font-semibold text-white">{pendingAction}</span>}
            </NavLink>
          )}
          {canAdmin && (
            <NavLink to="/admin" className={nav} title="Administração">
              <Settings className="size-4" /> <span className="hidden xl:inline">Administração</span>
            </NavLink>
          )}
        </nav>
        <div className="ml-auto flex shrink-0 items-center gap-1 md:gap-3">
          <span className="hidden md:inline-flex">
            <BusinessHoursBadge />
          </span>
          <span className="flex items-center gap-1.5 text-xs text-fg-soft" title={connected ? 'Atualizações em tempo real ativas' : 'Sem conexão em tempo real: reconectando'}>
            <span className={clsx('size-2 rounded-full', connected ? 'bg-ok' : 'animate-pulse bg-warn')} />
            <span className="hidden xl:inline">{connected ? 'online' : 'reconectando'}</span>
          </span>
          <SoftphoneButton />
          <NotificationCenter onOpenAttendance={openAttendance} />
          <NotifyMenu />
          {/* Tela larga: tema, nome (trocar senha) e sair à vista. Celular e telas menores que 1280 px: no menu. */}
          <button className="hidden rounded p-1.5 text-fg-soft hover:bg-muted xl:block" onClick={toggleTheme} title="Tema claro/escuro">
            {dark ? <Sun className="size-4" /> : <Moon className="size-4" />}
          </button>
          <button className="hidden max-w-40 truncate rounded px-1.5 py-1 text-sm hover:bg-muted xl:block" onClick={() => setChangingPassword(true)} title="Trocar minha senha">
            {user?.name}
          </button>
          <button className="hidden rounded p-1.5 text-fg-soft hover:bg-muted xl:block" onClick={() => void signOut()} title="Sair">
            <LogOut className="size-4" />
          </button>
          <div className="relative xl:hidden">
            <button className="rounded p-1.5 text-fg-soft hover:bg-muted" onClick={() => setUserMenu((m) => !m)} aria-label="Menu do usuário">
              <UserRound className="size-4" />
            </button>
            {userMenu && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setUserMenu(false)} />
                <div className="absolute top-full right-0 z-30 mt-1 w-56 rounded-md border border-border bg-panel py-1 text-sm shadow-lg">
                  <div className="truncate px-3 py-2 font-medium">{user?.name}</div>
                  {pwa.canInstall && (
                    <button className="flex w-full items-center gap-2 px-3 py-2 text-left text-accent hover:bg-muted" onClick={() => (setUserMenu(false), void pwa.install())}>
                      <Download className="size-4" /> Instalar o app
                    </button>
                  )}
                  <button className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted" onClick={() => (setUserMenu(false), toggleTheme())}>
                    {dark ? <Sun className="size-4" /> : <Moon className="size-4" />} Tema {dark ? 'claro' : 'escuro'}
                  </button>
                  <button className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted" onClick={() => (setUserMenu(false), setChangingPassword(true))}>
                    <KeyRound className="size-4" /> Trocar minha senha
                  </button>
                  <button className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted" onClick={() => void signOut()}>
                    <LogOut className="size-4" /> Sair
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>
      <main className="min-h-0 flex-1">
        <Routes>
          <Route path="/atendimento/:attendanceId?" element={<WorkspacePage />} />
          {canDashboard && <Route path="/painel" element={<DashboardPage onOpen={openAttendance} />} />}
          {canReports && <Route path="/relatorios" element={<ReportsPage onOpen={openAttendance} />} />}
          {canPending && <Route path="/pendencias" element={<PendingSyncPage onOpen={openAttendance} all={pendingAll} />} />}
          {canAdmin && <Route path="/admin/*" element={<AdminPage />} />}
          <Route path="*" element={<Navigate to="/atendimento" replace />} />
        </Routes>
      </main>
    </div>
  );
}
